import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Result, Spin } from 'antd';
import useAuthStore from '../store/authStore';
import { usePermissions } from '../hooks/usePermissions';

/**
 * Componente para proteger rutas que requieren autenticación
 */
export const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user, initialCheckDone } = useAuthStore();
  const location = useLocation();



  // Mostrar spinner durante la verificación inicial
  if (loading || !initialCheckDone) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    console.log('ProtectedRoute: Redirigiendo a login - No autenticado');
    // Redirigir al login manteniendo la ruta actual
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

/**
 * Componente para proteger rutas que requieren permisos específicos
 */
export const PermissionRoute = ({ 
  children, 
  permission, 
  roles, 
  fallback = null,
  showError = true 
}) => {
  const { hasPermission, hasAnyRole, isAuthenticated, userRole } = usePermissions();
  const { initialCheckDone, loading } = useAuthStore();


  // Esperar a que se complete la verificación inicial
  if (loading || !initialCheckDone) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    console.log('PermissionRoute: Redirigiendo a login - No autenticado');
    return <Navigate to="/login" replace />;
  }

  // Verificar por permiso específico
  if (permission && !hasPermission(permission)) {
    console.log('PermissionRoute: Acceso denegado por permiso:', permission);
    if (showError) {
      return (
        <Result
          status="403"
          title="403"
          subTitle="Lo siento, no tienes autorización para acceder a esta página."
          extra={fallback}
        />
      );
    }
    return fallback;
  }

  // Verificar por roles
  if (roles && !hasAnyRole(roles)) {
    console.log('PermissionRoute: Acceso denegado por rol. Usuario:', userRole, 'Requeridos:', roles);
    if (showError) {
      return (
        <Result
          status="403"
          title="403"
          subTitle="Lo siento, no tienes autorización para acceder a esta página."
          extra={fallback}
        />
      );
    }
    return fallback;
  }


  return children;
};

/**
 * HOC para proteger componentes que requieren autenticación
 */
export const withAuth = (Component) => {
  return function AuthenticatedComponent(props) {
    return (
      <ProtectedRoute>
        <Component {...props} />
      </ProtectedRoute>
    );
  };
};

/**
 * HOC para proteger componentes que requieren permisos específicos
 */
export const withPermission = (permission, options = {}) => (Component) => {
  return function PermissionProtectedComponent(props) {
    return (
      <PermissionRoute 
        permission={permission} 
        {...options}
      >
        <Component {...props} />
      </PermissionRoute>
    );
  };
};

/**
 * HOC para proteger componentes que requieren roles específicos
 */
export const withRoles = (roles, options = {}) => (Component) => {
  return function RoleProtectedComponent(props) {
    return (
      <PermissionRoute 
        roles={roles} 
        {...options}
      >
        <Component {...props} />
      </PermissionRoute>
    );
  };
};

export default ProtectedRoute;
